import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    TextField, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    TablePagination,
    InputAdornment,
    Skeleton,
    Button,
    Card,
    Collapse,
    MenuItem,
    IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { GuestService } from '../../api/services/guestService';
import { getCookie } from '../../utils/cookies';
import ExportFormatModal from '../../components/ExportFormatModal';
import { exportClientSide } from '../../utils/exportUtils';
import { toast } from '../../utils/toast';
import CreateGuestModal from './components/CreateGuestModal';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';
import GuestDetailsModal from './components/GuestDetailsModal';
import MergeGuestModal from './components/MergeGuestModal';
import AddIcon from '@mui/icons-material/Add';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';

export default function GuestManagement() {
    const authData = JSON.parse(getCookie('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';

    const [guests, setGuests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Pagination & Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(0); // MUI is 0-indexed
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [error, setError] = useState(null);
    
    // Modals
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [selectedGuestId, setSelectedGuestId] = useState(null);
    const [editGuestData, setEditGuestData] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const initialFilters = {
        country: '',
        state: '',
        city: '',
        nationality: '',
        idType: ''
    };
    const [filters, setFilters] = useState(initialFilters);
    const [debouncedFilters, setDebouncedFilters] = useState(initialFilters);

    const isFiltersDirty = searchQuery !== '' || Object.values(filters).some(val => val !== '');

    const fetchGuests = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: page + 1, // API expects 1-indexed
                limit: rowsPerPage,
            };
            if (debouncedSearch) {
                params.search = debouncedSearch;
            }
            // Append advanced filters, stripping out empty values
            Object.keys(debouncedFilters).forEach(key => {
                if (debouncedFilters[key]) {
                    params[key] = debouncedFilters[key];
                }
            });
            
            const result = await GuestService.searchGuests(params, tenantId);
            
            const guestList = Array.isArray(result) ? result : (result.data || result.guests || result.items || result.content || []);
            setGuests(guestList);
            
            const total = extractTotalCount(result, guestList);
            setTotalCount(total);
        } catch (err) {
            console.error("Failed to load guests directory:", err);
            // The global interceptor will show the toast
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce search query and advanced filters
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setDebouncedFilters(filters);
            setPage(0); // Reset to first page on new search/filter
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, filters]);

    // Fetch guests directory
    useEffect(() => {
        fetchGuests();
    }, [debouncedSearch, debouncedFilters, page, rowsPerPage, tenantId]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setSearchQuery('');
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = async (format) => {
        setIsLoading(true);
        setExportModalOpen(false);
        try {
            // Trigger bulk fetch with noPaging override
            const params = { noPagination: true };
            if (debouncedSearch) {
                params.search = debouncedSearch;
            }
            Object.keys(debouncedFilters).forEach(key => {
                if (debouncedFilters[key]) {
                    params[key] = debouncedFilters[key];
                }
            });
            const result = await GuestService.searchGuests(params, tenantId);
            const exportList = Array.isArray(result) ? result : (result.data || result.guests || []);
            
            exportClientSide(format, exportList, 'guest_profiles_directory');
            toast.success('Export successful');
            
            // Re-fetch standard view after export
            setPage(0);
        } catch (err) {
            console.error("Export failed:", err);
            toast.error(err.message || "Export failed");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold" color="text.primary">
                    Guest Profiles Directory
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setCreateModalOpen(true)}
                        disabled={isLoading}
                    >
                        Add Guest
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<CompareArrowsIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setMergeModalOpen(true)}
                        disabled={isLoading}
                        sx={standardSecondaryButtonSx}
                    >
                        Merge Guests
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setExportModalOpen(true)}
                        disabled={isLoading}
                        sx={standardSecondaryButtonSx}
                    >
                        Export
                    </Button>
                </Box>
                <ExportFormatModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onExport={handleExport}
                    isExporting={isLoading}
                    title="Export Guest Profiles"
                />
                <CreateGuestModal
                    open={createModalOpen}
                    onClose={() => {
                        setCreateModalOpen(false);
                        setEditGuestData(null);
                    }}
                    tenantId={tenantId}
                    editData={editGuestData}
                    onSuccess={() => {
                        setCreateModalOpen(false);
                        setEditGuestData(null);
                        fetchGuests();
                    }}
                />
                <MergeGuestModal
                    open={mergeModalOpen}
                    onClose={() => setMergeModalOpen(false)}
                    tenantId={tenantId}
                    onSuccess={() => fetchGuests()}
                />
            </Box>

            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search by name, email, or phone..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        label="Country"
                        name="country"
                        value={filters.country}
                        onChange={handleFilterChange}
                        sx={{ width: { xs: '100%', sm: 120 }, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        label="State"
                        name="state"
                        value={filters.state}
                        onChange={handleFilterChange}
                        sx={{ width: { xs: '100%', sm: 120 }, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        label="City"
                        name="city"
                        value={filters.city}
                        onChange={handleFilterChange}
                        sx={{ width: { xs: '100%', sm: 120 }, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        label="Nationality"
                        name="nationality"
                        value={filters.nationality}
                        onChange={handleFilterChange}
                        sx={{ width: { xs: '100%', sm: 120 }, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        label="ID Type"
                        name="idType"
                        value={filters.idType}
                        onChange={handleFilterChange}
                        sx={{ width: { xs: '100%', sm: 120 }, ...standardFilterInputSx }}
                    />
                    {isFiltersDirty && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            sx={standardResetButtonSx}
                            onClick={clearFilters}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Total Stays</TableCell>
                                <TableCell>Last Stay</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                Array.from(new Array(rowsPerPage)).map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                        <TableCell><Skeleton animation="wave" /></TableCell>
                                    </TableRow>
                                ))
                            ) : guests.length > 0 ? (
                                guests.map((guest) => (
                                    <TableRow 
                                        key={guest.id || guest.email} 
                                        hover 
                                    >
                                        <TableCell>{guest.name || 'N/A'}</TableCell>
                                        <TableCell>{guest.email || 'N/A'}</TableCell>
                                        <TableCell>{guest.phone || 'N/A'}</TableCell>
                                        <TableCell>{guest.totalStays ?? 0}</TableCell>
                                        <TableCell>{guest.lastStayDate ? new Date(guest.lastStayDate).toLocaleDateString() : 'N/A'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton 
                                                size="small" 
                                                color="primary"
                                                onClick={() => {
                                                    setSelectedGuestId(guest.id);
                                                    setDetailsModalOpen(true);
                                                }}
                                                sx={{ mr: 1 }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                color="info"
                                                onClick={() => {
                                                    setEditGuestData(guest);
                                                    setCreateModalOpen(true);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No guests found matching your criteria.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={guests.length === 0 ? 0 : totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>

            {/* Modals */}
            <GuestDetailsModal
                open={detailsModalOpen}
                onClose={() => {
                    setDetailsModalOpen(false);
                    setSelectedGuestId(null);
                }}
                guestId={selectedGuestId}
                tenantId={tenantId}
            />
        </Box>
    );
}
